/* Extends only the puzzle template. Native media-gallery and slideshow keep
 * variant updates, swipe, video/model playback and thumbnail selection. */
class MwhPuzzleLightbox extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    this.gallery = this.closest('media-gallery');
    this.dialog = this.querySelector('dialog');
    if (!this.gallery || !this.dialog) return;
    this.photo = this.querySelector('[data-mwh-photo]');
    this.stage = this.querySelector('[data-mwh-stage]');
    this.zoomButton = this.querySelector('[data-mwh-zoom]');
    this.photos = [...this.gallery.querySelectorAll('[data-mwh-open-image]')];
    const options = { signal: this.events.signal };
    this.setupMainPhotoGestures(options);
    this.gallery.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-mwh-open-image]');
      if (opener && this.gallery.contains(opener)) {
        event.preventDefault();
        // Ignore the click produced by a completed swipe, not a fresh tap.
        if (event.detail > 0 && performance.now() < (this.suppressOpenUntil || 0)) return;
        this.open(this.photos.indexOf(opener), opener);
      }
    }, { ...options, capture: true });
    this.querySelector('[data-mwh-close]').addEventListener('click', () => this.dialog.close(), options);
    this.querySelector('[data-mwh-previous]').addEventListener('click', () => this.show(this.index - 1), options);
    this.querySelector('[data-mwh-next]').addEventListener('click', () => this.show(this.index + 1), options);
    this.zoomButton.addEventListener('click', () => this.setZoom(!this.zoomed), options);
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
      }
    }, options);
    this.dialog.addEventListener('close', () => this.finishClose(), options);
    this.setupLightboxGestures(options);
    this.querySelectorAll('[data-mwh-previous], [data-mwh-next]').forEach((button) => {
      button.hidden = this.photos.length < 2;
    });
    this.setupModels();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.zoomFrame);
    if (this.dialog?.open) this.dialog.close();
    this.finishClose(false);
    this.events?.abort();
  }

  open(index, opener) {
    if (index < 0 || !this.photos.length) return;
    this.opener = opener;
    if (!this.dialog.open) {
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.lockedScroll = true;
      this.dialog.showModal();
    }
    this.show(index);
    this.querySelector('[data-mwh-close]').focus({ preventScroll: true });
  }

  show(index) {
    this.index = (index + this.photos.length) % this.photos.length;
    const source = this.photos[this.index];
    this.setZoom(false);
    this.photo.alt = source.dataset.imageAlt || '';
    this.photo.width = Number(source.dataset.imageWidth) || 1600;
    this.photo.height = Number(source.dataset.imageHeight) || 1600;
    this.photo.sizes = '100vw';
    this.photo.srcset = source.dataset.imageSrcset || '';
    this.photo.src = source.dataset.imageSrc;
    this.querySelector('[data-mwh-counter]').textContent = `Photo ${this.index + 1} of ${this.photos.length}`;
    const slide = source.closest('slideshow-slide');
    const slideshow = this.gallery.querySelector('slideshow-component');
    if (slide && typeof slideshow?.select === 'function') {
      const slideIndex = [...slideshow.querySelectorAll('slideshow-slide')].indexOf(slide);
      slideshow.select(slideIndex, undefined, { animate: false });
    }
  }

  setZoom(zoomed) {
    cancelAnimationFrame(this.zoomFrame);
    this.zoomed = zoomed;
    this.dialog.classList.toggle('is-zoomed', zoomed);
    this.zoomButton.setAttribute('aria-pressed', String(zoomed));
    this.zoomButton.textContent = zoomed ? 'Fit to screen' : 'Zoom in';
    this.photo.sizes = zoomed ? '200vw' : '100vw';
    // A portrait photo stays centered when enlarging the canvas.
    this.zoomFrame = requestAnimationFrame(() => {
      this.stage.scrollTop = zoomed ? (this.stage.scrollHeight - this.stage.clientHeight) / 2 : 0;
      this.stage.scrollLeft = zoomed ? (this.stage.scrollWidth - this.stage.clientWidth) / 2 : 0;
    });
  }

  setupMainPhotoGestures(options) {
    this.gallery.addEventListener('pointerdown', (event) => {
      if (!event.target.closest('[data-mwh-open-image]')) return;
      this.pointerStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.suppressOpenUntil = 0;
    }, { ...options, passive: true });
    this.gallery.addEventListener('pointermove', (event) => {
      if (!this.pointerStart || event.pointerId !== this.pointerStart.id) return;
      if (Math.hypot(event.clientX - this.pointerStart.x, event.clientY - this.pointerStart.y) > 12) {
        this.suppressOpenUntil = performance.now() + 500;
      }
    }, { ...options, passive: true });
    this.gallery.addEventListener('pointerup', () => { this.pointerStart = null; }, options);
    this.gallery.addEventListener('pointercancel', () => {
      if (this.pointerStart) this.suppressOpenUntil = performance.now() + 500;
      this.pointerStart = null;
    }, options);
    // Expand is a click target; the rest of the photo keeps native dragging.
    this.gallery.addEventListener('mousedown', (event) => {
      if (event.target.closest('[data-mwh-expand]')) event.stopPropagation();
    }, { ...options, capture: true });
  }

  setupLightboxGestures(options) {
    this.stage.addEventListener('pointerdown', (event) => {
      if (this.zoomed || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.stage.setPointerCapture(event.pointerId);
    }, options);
    this.stage.addEventListener('pointerup', (event) => {
      const start = this.swipe;
      this.swipe = null;
      if (!start || start.id !== event.pointerId || this.zoomed) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      const threshold = Math.max(36, Math.min(70, this.stage.clientWidth * 0.12));
      if (Math.abs(dx) >= threshold && Math.abs(dx) > Math.abs(dy) * 1.25) {
        this.show(this.index + (dx < 0 ? 1 : -1));
      }
    }, options);
    this.stage.addEventListener('pointercancel', () => { this.swipe = null; }, options);
    this.stage.addEventListener('dblclick', (event) => {
      event.preventDefault();
      this.setZoom(!this.zoomed);
    }, options);
    this.photo.addEventListener('dragstart', (event) => event.preventDefault(), options);
  }

  finishClose(restoreFocus = true) {
    if (this.lockedScroll) {
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.lockedScroll = false;
    }
    if (restoreFocus && this.opener?.isConnected) this.opener.focus({ preventScroll: true });
  }

  setupModels() {
    const modelJSON = this.gallery.querySelector('[id^="ProductModelJSON-"]');
    if (!modelJSON || !window.Shopify?.loadFeatures) return;
    if (!document.getElementById('mwh-model-viewer-style')) {
      const style = document.createElement('link');
      style.id = 'mwh-model-viewer-style';
      style.rel = 'stylesheet';
      style.href = 'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';
      document.head.append(style);
    }
    const register = () => {
      if (!window.ShopifyXR || !modelJSON.isConnected) return;
      try {
        window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
        window.ShopifyXR.setupXRElements();
      } catch (error) {
        console.warn('Unable to initialize product AR', error);
      }
    };
    window.Shopify.loadFeatures([{ name: 'shopify-xr', version: '1.0', onLoad: (error) => {
      if (error) return;
      if (window.ShopifyXR) register();
      else document.addEventListener('shopify_xr_initialized', register, { once: true, signal: this.events.signal });
    } }]);
  }
}

if (!customElements.get('mwh-puzzle-lightbox')) customElements.define('mwh-puzzle-lightbox', MwhPuzzleLightbox);
